// One card per sub-feature, each carrying its reference.
//
// This strip answers to one document and only that one. A card mixing another
// PRD's components could not be checked against either, because a reader
// cannot tell a missing component from one that belongs elsewhere.
import React from 'react';
import { FiArrowRight, FiLock } from 'react-icons/fi';
import s from '../TermPerformance.module.css';
import { num, pct } from '../format';

function Body({ card }) {
  switch (card.key) {
    case 'top':
      if (!card.movers?.length) return null;
      return (
        <ul className={s.cardList}>
          {card.movers.map((m) => (
            <li key={m.term}>
              <span className={s.cardTerm}>{m.term}</span>
              <span className={m.places > 0 ? s.up : s.down}>
                {m.places > 0 ? '▲' : '▼'} {Math.abs(m.places)}
              </span>
            </li>
          ))}
          {card.coverage && (
            <li className={s.cardNote}>
              {num(card.coverage.products_with_data)} of{' '}
              {num(card.coverage.products_in_catalogue)} products have data for this window.
            </li>
          )}
        </ul>
      );

    case 'conversion':
      return (
        <ul className={s.cardList}>
          <li className={s.cardNote}>
            {num(card.suppressed)} terms are held out of the ranking for having fewer clicks
            than a rate needs behind it. Their figures are still there — only the ranking
            suppresses them.
          </li>
        </ul>
      );

    case 'branded': {
      const parts = [['generic', card.generic], ['competitor', card.competitor],
                     ['branded', card.branded]];
      return (
        <>
          <div className={s.cardBar}>
            {parts.filter(([, v]) => v > 0).map(([k, v]) => (
              <span key={k} className={`${s.cardBarSeg} ${s[`mix_${k}`]}`}
                    style={{ width: `${v * 100}%` }} title={`${k}: ${pct(v, 0)}`} />
            ))}
          </div>
          <ul className={s.cardList}>
            {parts.map(([k, v]) => (
              <li key={k}>
                <span className={s.cardTerm}>
                  <span className={`${s.cardDot} ${s[`mix_${k}`]}`} />{' '}
                  {k === 'competitor' ? "a competitor's brand" : k}
                </span>
                <span className={s.cardFigure}>{pct(v, 0)}</span>
              </li>
            ))}
            <li className={s.cardNote}>
              On {num(card.total_clicks)} clicks, not on how many terms fall each way — a
              handful of branded terms can carry most of the traffic.
            </li>
          </ul>
        </>
      );
    }

    case 'gaps':
      if (!card.top?.length) return null;
      return (
        <ul className={s.cardList}>
          {card.top.map((t) => (
            <li key={t.term}>
              <span className={s.cardTerm}>{t.term}</span>
              <span className={s.cardFigure}>
                {pct(t.share, 1)} <span className={s.muted}>of {num(t.missed)} missed</span>
              </span>
            </li>
          ))}
        </ul>
      );

    case 'cannibalization':
      if (!card.top?.length) return null;
      return (
        <ul className={s.cardList}>
          {card.top.map((t) => (
            <li key={t.term}>
              <span className={s.cardTerm}>{t.term}</span>
              <span className={s.cardFigure}>
                {t.products} products · <strong>{t.leader}</strong>{' '}
                {pct(t.leader_clicks, 0)}
              </span>
            </li>
          ))}
        </ul>
      );

    default:
      return null;
  }
}

export default function FeatureCards({ data, loading, onOpen }) {
  if (loading && !data) {
    return (
      <div className={s.cardGrid}>
        {[0, 1, 2, 3, 4].map((i) => <div key={i} className={s.cardSkeleton} />)}
      </div>
    );
  }
  if (!data?.cards?.length) return null;

  return (
    <div className={s.cardGrid}>
      {data.cards.map((card) => {
        const clickable = card.state !== 'unavailable';
        return (
          <section key={card.key}
                   className={`${s.card} ${s[`card_${card.state}`]} ${clickable ? s.cardOpen : ''}`}
                   onClick={clickable ? () => onOpen(card.tab) : undefined}
                   role={clickable ? 'button' : undefined}
                   tabIndex={clickable ? 0 : undefined}
                   onKeyDown={clickable ? (e) => {
                     if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(card.tab); }
                   } : undefined}>
            <header className={s.cardHead}>
              <span className={s.cardRef}>{card.ref}</span>
              {clickable && <FiArrowRight className={s.cardGo} />}
              {!clickable && <FiLock className={s.cardLock} />}
            </header>
            <h3 className={s.cardTitle}>{card.title}</h3>
            <p className={s.cardQuestion}>{card.question}</p>
            <div className={s.cardHeadline}>
              <span className={s.cardValue}>{card.headline}</span>
              {card.unit && <span className={s.cardUnit}>{card.unit}</span>}
            </div>
            <p className={s.cardDetail}>{card.detail}</p>
            <Body card={card} />
          </section>
        );
      })}
    </div>
  );
}
